/**
 * Inline diff renderer — token-level segment painter.
 *
 * The backend (`comparison_engine._inline_diff`) ships a sequence of
 * `InlineDiffSegment` objects whose `text` chunks, when concatenated, round
 * trip the original string. This module turns that array into a sequence of
 * styled <span>s for use inside a single paragraph.
 *
 * Visual contract — `comparison-visual-language.md` §1.3:
 *   op="eq"  → unstyled text, inherits paragraph colour
 *   op="add" → addition green background
 *   op="del" → deletion red background + line-through
 *
 * Inline segments do **not** carry a left-side bar; bars are reserved for
 * paragraph-level change blocks (see `ChangeMarker`).
 *
 * Product invariant (memory: feedback_diff_text_visibility.md):
 * inline highlights MUST stay visible across all states — including after
 * an accept/reject decision. Accept/reject can be a misclick, and the
 * reviewer must always be able to verify which characters actually differ
 * between the two sides. Decision-skin contrast issues are solved by
 * adjusting the highlight palette, NOT by hiding the highlights.
 */
import { memo } from 'react';

import type { InlineDiffSegment } from '../../lib/veridoc-api';

interface InlineDiffViewProps {
  segments: InlineDiffSegment[];
  /**
   * Which side of the diff to render. The unified view shows every op; the
   * side-by-side view drops the opposing side's exclusive segments so each
   * column reads as the version it represents.
   *
   *   side="a" → drops `op === 'add'`  (left column shows version A)
   *   side="b" → drops `op === 'del'`  (right column shows version B)
   *   side="both" → renders every op (unified view default)
   */
  side?: 'a' | 'b' | 'both';
}

export function InlineDiffView({
  segments,
  side = 'both',
}: InlineDiffViewProps): React.JSX.Element {
  const visible = segments.filter((seg) => {
    // fmt ops are visible on both sides — the text is identical, only style differs
    if (side === 'a') return seg.op !== 'add';
    if (side === 'b') return seg.op !== 'del';
    return true;
  });

  return (
    <>
      {visible.map((seg, idx) => (
        <InlineDiffSegmentSpan key={idx} segment={seg} />
      ))}
    </>
  );
}

const InlineDiffSegmentSpan = memo(function InlineDiffSegmentSpan({
  segment,
}: {
  segment: InlineDiffSegment;
}): React.JSX.Element {
  switch (segment.op) {
    case 'eq':
      // No wrapper for equal text — keeps copy/paste output clean and lets
      // the paragraph's own typography shine through.
      return <>{segment.text}</>;
    case 'add':
      return (
        <span
          className="rounded-sm bg-change-addition-bg px-0.5 font-bold text-change-addition-fg"
          aria-label="新增片段"
        >
          {segment.text}
        </span>
      );
    case 'del':
      return (
        <span
          className="rounded-sm bg-change-deletion-bg px-0.5 font-bold text-change-deletion-fg line-through decoration-change-deletion-bar decoration-2"
          aria-label="删除片段"
        >
          {segment.text}
        </span>
      );
    case 'fmt': {
      // Render with a dashed underline to indicate a format-only change.
      // The title attribute provides detail on which style flag changed.
      const fromLabel = segment.styleA ?? 'plain';
      const toLabel = segment.styleB ?? 'plain';
      return (
        <span
          className="border-b border-dashed border-change-format-bar text-change-format-fg"
          aria-label="格式变更片段"
          title={`格式：${fromLabel} → ${toLabel}`}
        >
          {segment.text}
        </span>
      );
    }
    default: {
      const _exhaustive: never = segment.op;
      void _exhaustive;
      return <>{segment.text}</>;
    }
  }
});

export default InlineDiffView;
